import { DOCUMENT, ViewportScroller } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { NavbarPosition } from '@features/homeB/navbar-section-home-b/navbar-section-home-b.component';
import { Section, SectionIdEnum, SECTIONS } from '@features/homeB/sections';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { trackEventWithAtInternet } from '@wizbii/tracking';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

interface SectionsWithVisibility {
  [key: number]: { sectionId: SectionIdEnum; isVisible: boolean };
}
@UntilDestroy()
@Component({
  templateUrl: './home-b.component.html',
  styleUrls: ['./home-b.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeBComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('sectionsList', { read: ElementRef }) sectionsList!: QueryList<ElementRef>;
  @ViewChild('hero', { read: ElementRef }) heroComponent!: ElementRef;
  @ViewChild('sectionContainer', { read: ElementRef }) sectionContainer!: ElementRef;
  @ViewChild('employerSection', { read: ElementRef }) employerSection!: ElementRef;

  sectionsListObserver!: IntersectionObserver;
  heroObserver!: IntersectionObserver;
  sectionsContainerObserver!: IntersectionObserver;
  employerSectionObserver!: IntersectionObserver;

  sections!: Section[];
  isHeroIsVisible = false;

  navbarPosition$ = new BehaviorSubject<NavbarPosition>(NavbarPosition.top);
  navItemIndexSelected$ = new BehaviorSubject<number>(0);
  suspendObservation$ = new BehaviorSubject<boolean>(false);

  heroIsVisible$ = new BehaviorSubject<boolean>(true);
  sectionsContainerIsVisible$ = new BehaviorSubject<boolean>(true);
  employerSectionIsVisible$ = new BehaviorSubject<boolean>(true);

  sectionsWithVisibility$ = new BehaviorSubject<SectionsWithVisibility>({
    0: { sectionId: SectionIdEnum.jobs, isVisible: true },
    1: { sectionId: SectionIdEnum.trainings, isVisible: false },
    2: { sectionId: SectionIdEnum.helps, isVisible: false },
    3: { sectionId: SectionIdEnum.engagment, isVisible: false },
  });

  trackEvent = trackEventWithAtInternet;

  get isMobile$(): Observable<boolean> {
    return this.breakpointsService.isMobile$;
  }

  constructor(
    private readonly breakpointsService: BreakpointsService,
    private readonly route: ActivatedRoute,
    @Inject(DOCUMENT) private readonly document: any,
    private readonly viewportScroller: ViewportScroller
  ) {}

  ngAfterViewInit(): void {
    if (this.sectionsList) {
      this.sectionsList.forEach((el) => this.sectionsListObserver.observe(el.nativeElement));
    }

    if (this.employerSection) {
      this.employerSectionObserver.observe(this.employerSection.nativeElement);
    }

    if (this.heroComponent) {
      this.heroObserver.observe(this.heroComponent.nativeElement);
    }

    if (this.sectionContainer) {
      this.sectionsContainerObserver.observe(this.sectionContainer.nativeElement);
    }

    this.initFragment();
  }

  ngOnInit(): void {
    this.sections = SECTIONS;

    this.employerSectionObserver = new IntersectionObserver((entries) =>
      entries.forEach((entry) => this.employerSectionIsVisible$.next(entry.isIntersecting))
    );

    this.heroObserver = new IntersectionObserver((entries, _) => {
      entries.forEach(
        (entry: IntersectionObserverEntry) => {
          this.heroIsVisible$.next(entry.isIntersecting);
        },
        { rootMargin: '5px 0px 5px 0px' }
      );
    });

    this.sectionsContainerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry: IntersectionObserverEntry) => {
          this.sectionsContainerIsVisible$.next(entry.isIntersecting);
        });
      },
      { rootMargin: '-140px 0px -140px 0px' }
    );

    this.sectionsListObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = this.getSelectionSection(entry.target.id as SectionIdEnum);

          const obj: SectionsWithVisibility = {};

          obj[index] = {
            sectionId: entry.target.id as SectionIdEnum,
            isVisible: entry.isIntersecting,
          };

          this.sectionsWithVisibility$.next({ ...this.sectionsWithVisibility$.value, ...obj });
        });
      },
      { rootMargin: '-100px 0px -100px 0px' }
    );

    combineLatest([
      this.sectionsWithVisibility$,
      this.heroIsVisible$,
      this.sectionsContainerIsVisible$,
      this.employerSectionIsVisible$,
      this.isMobile$,
    ])
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe({
        next: ([
          sectionsWithVisibility,
          isHeroVisible,
          sectionsContainerIsVisible,
          employerSectionIsVisible,
          isMobile,
        ]: [SectionsWithVisibility, boolean, boolean, boolean, boolean]): void => {
          const navbarPosition: NavbarPosition = this.manageNavbarPosition({
            isHeroVisible,
            sectionsContainerIsVisible,
            isMobile,
          });

          const targetSection = Object.values(sectionsWithVisibility).find((i) => i.isVisible);

          if (targetSection) {
            const sectionIndex = this.getSelectionSection(targetSection.sectionId as SectionIdEnum);
            this.navItemIndexSelected$.next(sectionIndex);
          }

          this.navbarPosition$.next(navbarPosition);

          if (employerSectionIsVisible) {
            this.suspendObservation$.next(false);
          }
        },
      });
  }

  initFragment(): void {
    const fragment = this.route.snapshot.fragment;
    fragment && this.viewportScroller.scrollToAnchor(fragment);
  }

  getSelectionSection(sectionId: SectionIdEnum): number {
    return this.sections.findIndex((s) => s.id === sectionId);
  }

  manageNavbarPosition({
    isHeroVisible,
    sectionsContainerIsVisible,
    isMobile,
  }: {
    isHeroVisible: boolean;
    sectionsContainerIsVisible: boolean;
    isMobile: boolean;
  }): NavbarPosition {
    if (isMobile) {
      return sectionsContainerIsVisible ? NavbarPosition.mobile : NavbarPosition.hidden;
    }

    if (this.suspendObservation$.value) {
      return NavbarPosition.top;
    }

    if (!sectionsContainerIsVisible) {
      return NavbarPosition.hidden;
    }

    return isHeroVisible ? NavbarPosition.top : NavbarPosition.fixed;
  }

  onClickedNavItem(navItemValue: string): void {
    const navItem = this.sections.find((i) => i.id === navItemValue);
    navItem && this.trackEvent({ category: 'Home', action: 'Click Menu', label: navItem.shortTitle, type: 'action' });
    this.scrollToElementId(navItemValue);
  }

  hideNavbar(): void {
    this.suspendObservation$.next(true);
  }

  trackBySection(_: number, section: Section): string {
    return section.title;
  }

  private scrollToElementId(elementId: string): void {
    this.document.getElementById(elementId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'center',
    });
  }

  ngOnDestroy(): void {
    this.navbarPosition$.complete();
    this.navItemIndexSelected$.complete();
    this.suspendObservation$.complete();
    this.heroIsVisible$.complete();
    this.sectionsContainerIsVisible$.complete();
    this.employerSectionIsVisible$.complete();
    this.sectionsWithVisibility$.complete();
  }
}
